import React, { FC } from "react";
import { Button, IconButton, Loader, SystemMessage, Text, Tooltip } from "@shoptet/ui";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import googleImage from "../assets/google-brand.svg";
import checkIcon from "../assets/icons/check.svg";
import insightIcon from "../assets/icons/insight.svg";
import neutralInfoIcon from "../assets/icons/neutral_info.svg";
import stoppedIcon from "../assets/icons/stopped.svg";
import warningIcon from "../assets/icons/warning.svg";
import { WIZARD_STEP_PATH } from "../constants/paths";
import {
  AdSystemStateT,
  AdSystemT,
  CreditsDocumentT,
  MissingShippingStatusT,
  PaymentStateT,
  useAdSystemCampaignStateQuery,
  useChangeGoogleAdsSettingsStateMutation,
  useChangeMetaAdsSettingsStateMutation,
  useCreditsQuery,
  useOrganizationPaymentsByAdSystemQuery,
  useOrganizationQuery,
  useStartSynchronizationMutation,
} from "../graphql/generated/graphql";
import { useAssetPublish } from "../hooks/useAssetPublish";
import { useHasDisapprovedAssets } from "../hooks/useHasDisapprovedAssets";
import { useHasLowPerformanceAssets } from "../hooks/useHasLowPerformanceAssets";
import { useWizardState } from "../hooks/useWizardState";
import { formatCurrencyUnit, formatMoney } from "../i18n/formatNumbers";
import { clearBudgetModalReturn, readBudgetModalReturn } from "../utils/budgetModalReturn";
import { getAdSystemCampaignState } from "../utils/getAdSystemCampaignState";
import { DashboardCreditsModal } from "./Credits/DashboardCreditsModal";
import { MetaLogo } from "./MetaLogo";
import { ModalWrapper } from "./ModalWrapper";
import { EditBudgetModal } from "./MonthlyBudget/EditBudgetModal";
import { SmallLoader } from "./SmallLoader";

const STOPPED_CAMPAIGN_STATES = ["paused", "no_credit", "no_products", "account_suspended"];

const isStoppedCampaignState = (campaignState: string) => STOPPED_CAMPAIGN_STATES.includes(campaignState);

const isActiveCampaignState = (campaignState: string) =>
  campaignState === "active" || campaignState === "active_optimizing";

// A campaign that is running but has no impressions yet resolves to "activating".
// When all products are missing shipping they can never serve, so the state stays
// "activating" indefinitely — treat it as running for limited-mode messaging.
const isRunningCampaignState = (campaignState: string) =>
  isActiveCampaignState(campaignState) || campaignState === "activating";

interface AdSystemBudgetBoxProps {
  adSystem?: AdSystemT;
  onNavigateToAssets?: () => void;
  onNavigateToRecommendations?: () => void;
}

interface CampaignStateProps {
  adSystem: AdSystemT;
  allProductsMissingShipping: boolean;
  campaignState: string;
  googleShoppingSuspended: boolean;
  showPaymentProcessing: boolean;
  wizardFinished: boolean;
}

const PaymentProcessingAnnouncement = () => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <SystemMessage
          level="notice"
          description={
            <Text>
              <Trans components={{ b: <b /> }} i18nKey="Pending initiated announcement description text" t={t} />
            </Text>
          }
        />
      </div>
    </>
  );
};

const PaymentFailureAnnouncement = ({ adSystem }: { adSystem: AdSystemT }) => {
  const [showAddCreditsModal, setShowAddCreditsModal] = React.useState(false);
  const { t } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <SystemMessage
          level="error"
          controls={
            <Button variant="primary" onClick={() => setShowAddCreditsModal(true)}>
              {t("Add credits")}
            </Button>
          }
          description={
            <Text>
              <Trans
                components={{ a: <a />, b: <b /> }}
                i18nKey="Payment failure announcement description text"
                t={t}
              />
            </Text>
          }
        />
      </div>
      <DashboardCreditsModal
        isOpen={showAddCreditsModal}
        startCampaignFor={adSystem}
        onClose={() => setShowAddCreditsModal(false)}
      />
    </>
  );
};

const ActivateAfterFailureAnnouncement = ({ adSystem }: { adSystem: AdSystemT }) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  const [updateInProgress, setUpdateInProgress] = React.useState(false);
  const [startSynchronization] = useStartSynchronizationMutation({
    refetchQueries: [CreditsDocumentT],
    awaitRefetchQueries: true,
  });

  const activateCampaign = async () => {
    setUpdateInProgress(true);
    try {
      await startSynchronization({ variables: { adSystem } });
    } finally {
      setUpdateInProgress(false);
    }
  };

  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <SystemMessage
          level="notice"
          controls={
            <Button
              disabled={updateInProgress}
              isLoading={updateInProgress}
              variant="primary"
              onClick={activateCampaign}
            >
              {t("Activate campaigns")}
            </Button>
          }
          description={
            <Text>
              <Trans components={{ b: <b /> }} i18nKey="Activate after failure announcement description text" t={t} />
            </Text>
          }
        />
      </div>
    </>
  );
};

const ActivatingAnnouncement = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const { t } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <SystemMessage
          level="notice"
          controls={
            <Button variant="secondary" onClick={() => setIsModalOpen(true)}>
              {t("Learn more")}
            </Button>
          }
          description={
            <Text>
              <div>
                <Trans components={{ b: <b /> }} i18nKey="Activating announcement description text 1" t={t} />
              </div>
            </Text>
          }
        />
      </div>
      <ModalWrapper
        isOpen={isModalOpen}
        title={t("Activating announcement modal text title")}
        maxWidth
        onClose={() => setIsModalOpen(false)}
      >
        <Text>
          <Trans components={{ a: <a /> }} i18nKey={t("Activating announcement modal text")} t={t} />
        </Text>
      </ModalWrapper>
    </>
  );
};

const OPTIMIZATION_DISMISSED_KEY = "shoptet_campaign_optimized_dismissed";

const CampaignOptimizedAnnouncement = ({ adSystem }: { adSystem: AdSystemT }) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  const storageKey = `${OPTIMIZATION_DISMISSED_KEY}_${adSystem}`;
  const [dismissed, setDismissed] = React.useState(() => localStorage.getItem(storageKey) === "true");

  if (dismissed) {
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(storageKey, "true");
    setDismissed(true);
  };

  return (
    <div style={{ marginBottom: "16px" }}>
      <SystemMessage
        controls={<IconButton icon="shp-cross" variant="secondary" ghost onClick={handleDismiss} />}
        level="notice"
        title={t("Your campaign was optimized title")}
        description={
          <Text>
            <Trans
              components={{ a: <a target="blank" />, b: <b /> }}
              i18nKey="Your campaign was optimized description"
              t={t}
            />
          </Text>
        }
      />
    </div>
  );
};

const LearningAnnouncement = () => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const { t } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <SystemMessage
          level="notice"
          controls={
            <Button variant="secondary" onClick={() => setIsModalOpen(true)}>
              {t("Learn more")}
            </Button>
          }
          description={
            <Text>
              <Trans components={{ b: <b /> }} i18nKey="Learning announcement description text" t={t} />
            </Text>
          }
        />
      </div>
      <ModalWrapper
        isOpen={isModalOpen}
        title={t("Learning announcement modal title")}
        maxWidth
        onClose={() => setIsModalOpen(false)}
      >
        <div style={{ marginBottom: "16px" }}>
          <Text>
            <Trans components={{ b: <b /> }} i18nKey={t("Learning announcement modal text 1")} t={t} />
          </Text>
        </div>
        <Text>
          <Trans components={{ a: <a />, b: <b /> }} i18nKey={t("Learning announcement modal text 2")} t={t} />
        </Text>
      </ModalWrapper>
    </>
  );
};

interface DissaprovedAssetsAnnouncementProps {
  onNavigateToAssets?: () => void;
}

const DissaprovedAssetsAnnouncement = ({ onNavigateToAssets }: DissaprovedAssetsAnnouncementProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <SystemMessage
          level="alert"
          controls={
            <Button variant="secondary" onClick={onNavigateToAssets}>
              {t("Edit")}
            </Button>
          }
          description={
            <Text>
              <Trans components={{ b: <b /> }} i18nKey="Dissaproved assets announcement description text" t={t} />
            </Text>
          }
        />
      </div>
    </>
  );
};

const LowPerformanceAssetsAnnouncement = ({ onNavigateToAssets }: DissaprovedAssetsAnnouncementProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <SystemMessage
          level="alert"
          controls={
            <Button variant="secondary" onClick={onNavigateToAssets}>
              {t("Edit")}
            </Button>
          }
          description={
            <Text>
              <Trans components={{ b: <b /> }} i18nKey="Low performance assets announcement description text" t={t} />
            </Text>
          }
        />
      </div>
    </>
  );
};

interface CampaignStoppedByUserAnnouncementProps {
  adSystem: AdSystemT;
  conversionsCount?: number;
}

const CampaignStoppedByUserAnnouncement = ({
  adSystem,
  conversionsCount = 0,
}: CampaignStoppedByUserAnnouncementProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  const [updateInProgress, setUpdateInProgress] = React.useState(false);
  const [changeGoogleAdsSettingsMutation] = useChangeGoogleAdsSettingsStateMutation({
    onCompleted: () => {
      setUpdateInProgress(false);
    },
  });
  const [changeMetaAdsSettingsMutation] = useChangeMetaAdsSettingsStateMutation({
    onCompleted: () => {
      setUpdateInProgress(false);
    },
  });

  const changeCampaignSetting = (state: AdSystemStateT) => {
    setUpdateInProgress(true);
    if (adSystem === AdSystemT.GoogleAdsT) {
      return changeGoogleAdsSettingsMutation({ variables: { input: { state } } });
    } else {
      return changeMetaAdsSettingsMutation({ variables: { input: { state } } });
    }
  };

  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <SystemMessage
          level="error"
          controls={
            <Button
              disabled={updateInProgress}
              isLoading={updateInProgress}
              variant="primary"
              onClick={() => changeCampaignSetting(AdSystemStateT.ActiveT)}
            >
              {t("Launch campaign")}
            </Button>
          }
          description={
            <Text>
              <Trans
                components={{ b: <b /> }}
                t={t}
                values={{ conversionsCount }}
                i18nKey={
                  conversionsCount === 0
                    ? "Campaign Stopped by user Announcement announcement description text zero"
                    : "Campaign Stopped by user Announcement announcement description text"
                }
              />
            </Text>
          }
        />
      </div>
    </>
  );
};

interface UnpublishedChangesAnnouncementProps {
  canPublish: boolean;
  loadingPublish: boolean;
  onPublish: () => void;
}

const UnpublishedChangesAnnouncement = ({
  canPublish,
  loadingPublish,
  onPublish,
}: UnpublishedChangesAnnouncementProps) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <SystemMessage
          level="notice"
          controls={
            canPublish ? (
              <Button disabled={loadingPublish} isLoading={loadingPublish} variant="primary" onClick={onPublish}>
                {t("Publish changes")}
              </Button>
            ) : undefined
          }
          description={
            <Text>
              <Trans
                components={{ b: <b /> }}
                i18nKey="Unpublished changes Announcement announcement description text"
                t={t}
              />
            </Text>
          }
        />
      </div>
    </>
  );
};

const GoogleShoppingSuspendedAnnouncement = () => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <SystemMessage
          level="alert"
          description={
            <Text>
              <Trans
                components={{ a: <a />, b: <b /> }}
                i18nKey="Google Shopping suspended announcement description text"
                t={t}
              />
            </Text>
          }
        />
      </div>
    </>
  );
};

const MissingShippingAnnouncement = ({ onNavigateToRecommendations }: { onNavigateToRecommendations?: () => void }) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  const helpUrl = t("Missing shipping help url");
  const supportEmail = t("Missing shipping support email");
  return (
    <div style={{ marginBottom: "16px" }}>
      <SystemMessage
        level="error"
        controls={
          <Button variant="primary" onClick={onNavigateToRecommendations}>
            {t("Missing shipping recommendation button")}
          </Button>
        }
        description={
          <Text>
            <Trans
              i18nKey="Missing shipping all announcement description text"
              t={t}
              components={{
                a: <a href={helpUrl} rel="noopener noreferrer" target="_blank" />,
                b: <b />,
                email: <a href={`mailto:${supportEmail}`} />,
              }}
            />
          </Text>
        }
      />
    </div>
  );
};

const CampaignStoppedBySystemAnnouncement = ({ adSystem }: { adSystem: AdSystemT }) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  return (
    <>
      <div style={{ marginBottom: "16px" }}>
        <SystemMessage
          level="error"
          description={
            <Text>
              <Trans
                components={{ a: <a />, b: <b /> }}
                i18nKey="Campaign Stopped by system Announcement announcement description text"
                t={t}
                values={{ system: adSystem === AdSystemT.GoogleAdsT ? "Google" : "Meta" }}
              />
            </Text>
          }
        />
      </div>
    </>
  );
};

const CampaignState: FC<CampaignStateProps> = ({
  adSystem,
  allProductsMissingShipping,
  campaignState,
  googleShoppingSuspended,
  showPaymentProcessing,
  wizardFinished,
}) => {
  const { t } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  const isActiveLimitedMode =
    (isRunningCampaignState(campaignState) && allProductsMissingShipping) ||
    (isActiveCampaignState(campaignState) && googleShoppingSuspended);
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
        {adSystem === AdSystemT.GoogleAdsT && (
          <div
            style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "83px", height: "28px" }}
          >
            <img alt="Google" src={googleImage} style={{ display: "block", width: "100%", height: "auto" }} />
          </div>
        )}
        {adSystem === AdSystemT.MetaAdsT && (
          <div
            style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "87px", height: "28px" }}
          >
            <MetaLogo width={87} />
          </div>
        )}
        {isActiveLimitedMode && (
          <Tooltip
            content={t(
              googleShoppingSuspended ? "State limited mode google shopping tooltip" : "State limited mode tooltip"
            )}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "help" }}>
              <img alt="warning" src={warningIcon} style={{ width: "16px" }} />
              <Text appearance="strong-md">
                <span data-test-id="campaign-state" style={{ color: "var(--app-warning)" }}>
                  {t("State limited mode")}
                </span>
              </Text>
            </div>
          </Tooltip>
        )}
        {isActiveCampaignState(campaignState) && !isActiveLimitedMode && (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <img alt="check" src={checkIcon} style={{ width: "16px" }} />
            <Text appearance="strong-md">
              <span data-test-id="campaign-state" style={{ color: "var(--app-success)" }}>
                {t("State active")}
              </span>
            </Text>
          </div>
        )}
        {!wizardFinished && !showPaymentProcessing && (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <img alt="insight" src={insightIcon} style={{ width: "16px" }} />
            <Text appearance="strong-md">
              <span data-test-id="campaign-state" style={{ color: "var(--app-text-muted)" }}>
                {t("Activate your campaigns")}
              </span>
            </Text>
          </div>
        )}
        {(campaignState === "activating" || showPaymentProcessing) && !isActiveLimitedMode && (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <Loader size="sm" />
            <Text appearance="strong-md">
              <span data-test-id="campaign-state" style={{ color: "var(--app-primary)" }}>
                {t("State activating short")}
              </span>
            </Text>
          </div>
        )}
        {isStoppedCampaignState(campaignState) && wizardFinished && (
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <img alt="stopped" src={stoppedIcon} style={{ width: "16px" }} />
            <Text appearance="strong-md">
              <span data-test-id="campaign-state" style={{ color: "var(--app-critical)" }}>
                {t("State paused")}
              </span>
            </Text>
          </div>
        )}
      </div>
    </div>
  );
};

const AdSystemBudgetBox = ({
  adSystem = AdSystemT.GoogleAdsT,
  onNavigateToAssets,
  onNavigateToRecommendations,
}: AdSystemBudgetBoxProps) => {
  const [showEditBudgetModal, setShowEditBudgetModal] = React.useState(false);
  const [editBudgetInitialValue, setEditBudgetInitialValue] = React.useState<number | undefined>(undefined);

  React.useEffect(() => {
    const stored = readBudgetModalReturn(adSystem);
    if (stored) {
      clearBudgetModalReturn();
      setEditBudgetInitialValue(stored.budget);
      setShowEditBudgetModal(true);
    }
  }, [adSystem]);

  const { t } = useTranslation("translation", { keyPrefix: "Components.MonthlyBudget" });
  const { t: tCampaignState } = useTranslation("translation", { keyPrefix: "Components.CampaignState" });
  const navigate = useNavigate();
  const { data, loading, startPolling, stopPolling } = useAdSystemCampaignStateQuery({
    variables: {
      adSystem,
      dateTo: new Date().toISOString().split("T")[0],
    },
  });

  const { data: paymentsData } = useOrganizationPaymentsByAdSystemQuery({
    variables: {
      startCampaignFor: adSystem,
      limit: 100,
      page: 1,
    },
  });

  const { data: creditsData } = useCreditsQuery();
  const creditsBalance = creditsData?.organization?.creditBalance?.amount || 0;
  const recommendedBudget = creditsData?.organization?.recommendedMonthlyBudget?.amount || 3000;

  const { data: orgData } = useOrganizationQuery();
  const allProductsMissingShipping =
    orgData?.organization?.missingShippingStatus === MissingShippingStatusT.AllProductsT;
  const googleShoppingSuspended =
    adSystem === AdSystemT.GoogleAdsT && Boolean(orgData?.organization?.googleShoppingSuspended);
  const hasAnyDisapproved = useHasDisapprovedAssets();
  const hasAnyLowPerformance = useHasLowPerformanceAssets(adSystem);
  const { canPublish, hasAnyDrafts, loadingPublish, publish } = useAssetPublish();
  const { googleCurrentStep, googleWizardFinished, metaCurrentStep, metaWizardFinished } = useWizardState();
  const [changeGoogleAdsSettingsState] = useChangeGoogleAdsSettingsStateMutation();
  const [changeMetaAdsSettingsState] = useChangeMetaAdsSettingsStateMutation();
  const adsSetting =
    adSystem === AdSystemT.GoogleAdsT ? data?.organization?.googleAdsSetting : data?.organization?.metaAdsSetting;
  const monthlyBudgetAmount = adsSetting?.monthlyBudget?.amount || 0;
  const currency = adsSetting?.monthlyBudget?.currency || "CZK";
  const campaignState = getAdSystemCampaignState(data, adSystem);

  React.useEffect(() => {
    if (campaignState === "wizard") {
      startPolling(1500);
    } else {
      stopPolling();
    }
    return () => {
      stopPolling();
    };
  }, [campaignState, startPolling, stopPolling]);

  const conversionsCount = Math.round(data?.organization?.summaryStats?.conversions || 0);
  const payments = paymentsData?.organization?.payments || [];
  const payment = payments[0];
  const wizardFinished = adSystem === AdSystemT.GoogleAdsT ? googleWizardFinished : metaWizardFinished;
  const isWizardState = campaignState === "wizard";
  const paymentState = payment?.state;
  const paymentFailed = isWizardState && paymentState === PaymentStateT.FailureT;
  const hasEnoughCredits = creditsBalance > recommendedBudget;
  const showPaymentProcessing =
    isWizardState &&
    (paymentState === PaymentStateT.InitiatedT || (paymentState === PaymentStateT.SuccessT && wizardFinished));
  const showPaymentFailure = paymentFailed && !hasEnoughCredits;
  const showActivateAfterFailure = paymentFailed && hasEnoughCredits;
  const showActivatingAnnouncement = campaignState === "activating";
  const isCampaignPaused = isStoppedCampaignState(campaignState);

  const renderBudgetMessage = () => {
    if (isCampaignPaused || showActivatingAnnouncement || showPaymentProcessing) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "16px",
              height: "16px",
              background: "var(--app-primary)",
              borderRadius: "50%",
            }}
          >
            <img alt="info" src={neutralInfoIcon} />
          </div>
          <div>
            <Text>{t("Budget is ready to go")}</Text>
          </div>
        </div>
      );
    } else {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "3px" }}>
          <img alt="check" src={checkIcon} />
          <div>
            <Text>{t("Budget is optimal")}</Text>
          </div>
        </div>
      );
    }
  };
  return (
    <>
      <div
        style={{ border: "1px solid var(--app-border)", borderRadius: "var(--app-radius-box)", padding: "16px 16px 0" }}
      >
        {loading && <SmallLoader />}
        {data && !loading && (
          <>
            <div>
              <CampaignState
                adSystem={adSystem}
                allProductsMissingShipping={allProductsMissingShipping}
                campaignState={campaignState}
                googleShoppingSuspended={googleShoppingSuspended}
                showPaymentProcessing={showPaymentProcessing}
                wizardFinished={wizardFinished}
              />
            </div>
            {(wizardFinished || showPaymentProcessing) && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  padding: "16px",
                  marginBottom: "16px",
                  border: "1px solid var(--app-border)",
                  borderRadius: "var(--app-radius-box)",
                }}
              >
                <div>
                  <Text appearance="strong-lg">{t("Label")}</Text>
                  <div style={{ margin: "4px 0 8px" }}>
                    <Text data-test-id="current_monthly_budget">
                      <span
                        style={{
                          fontWeight: "bold",
                          fontSize: "16px",
                          color:
                            isCampaignPaused || showActivatingAnnouncement
                              ? "var(--app-text-muted)"
                              : "var(--app-primary)",
                        }}
                      >{`${formatMoney({
                        amount: monthlyBudgetAmount,
                      })} ${formatCurrencyUnit(currency)}`}</span>
                    </Text>
                  </div>
                  {renderBudgetMessage()}
                </div>
                <div>
                  <Button
                    data-test-id="open-monthly-budget-modal-button"
                    variant="secondary"
                    onClick={() => setShowEditBudgetModal(true)}
                  >
                    {t("Edit")}
                  </Button>
                </div>
              </div>
            )}
            {!wizardFinished && !showPaymentProcessing && (
              <>
                {adSystem === AdSystemT.GoogleAdsT && (
                  <div>
                    <div style={{ marginBottom: "6px" }}>
                      <Text appearance="strong-md">
                        {tCampaignState("Find more clients thanks to Google campaigns")}
                      </Text>
                    </div>
                    <div style={{ marginBottom: "16px" }}>
                      <Text>{tCampaignState("Start advertise on Google ...")}</Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                      <Button
                        variant="primary"
                        onClick={async () => {
                          await changeGoogleAdsSettingsState({
                            variables: { input: { state: AdSystemStateT.WizardT } },
                          });
                          navigate(WIZARD_STEP_PATH[googleCurrentStep]);
                        }}
                      >
                        {tCampaignState("Launch Google campaign")}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() =>
                          window.open(
                            tCampaignState("Learn more Google campaign cta url"),
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        {tCampaignState("Learn more")}
                      </Button>
                    </div>
                  </div>
                )}
                {adSystem === AdSystemT.MetaAdsT && (
                  <div>
                    <div style={{ marginBottom: "6px" }}>
                      <Text appearance="strong-md">{tCampaignState("Find more clients thanks to Meta campaigns")}</Text>
                    </div>
                    <div style={{ marginBottom: "16px" }}>
                      <Text>{tCampaignState("Launch campaign to facebook directly from Shoptet")}</Text>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "16px" }}>
                      <Button
                        variant="primary"
                        onClick={async () => {
                          await changeMetaAdsSettingsState({
                            variables: { input: { state: AdSystemStateT.WizardT } },
                          });
                          navigate((WIZARD_STEP_PATH as Record<string, string>)[metaCurrentStep]);
                        }}
                      >
                        {tCampaignState("Launch Meta campaign")}
                      </Button>
                      <Button
                        variant="secondary"
                        onClick={() =>
                          window.open(
                            tCampaignState("Learn more Meta campaign cta url"),
                            "_blank",
                            "noopener,noreferrer"
                          )
                        }
                      >
                        {tCampaignState("Learn more")}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
        {showPaymentProcessing && <PaymentProcessingAnnouncement />}
        {showPaymentFailure && <PaymentFailureAnnouncement adSystem={adSystem} />}
        {showActivateAfterFailure && <ActivateAfterFailureAnnouncement adSystem={adSystem} />}
        {showActivatingAnnouncement && <ActivatingAnnouncement />}
        {adsSetting?.learningState && !showActivatingAnnouncement && <LearningAnnouncement />}
        {campaignState === "active_optimizing" && <CampaignOptimizedAnnouncement adSystem={adSystem} />}
        {adSystem === AdSystemT.GoogleAdsT && hasAnyDisapproved && (
          <DissaprovedAssetsAnnouncement onNavigateToAssets={onNavigateToAssets} />
        )}
        {hasAnyLowPerformance && <LowPerformanceAssetsAnnouncement onNavigateToAssets={onNavigateToAssets} />}
        {adSystem === AdSystemT.GoogleAdsT && hasAnyDrafts && (
          <UnpublishedChangesAnnouncement canPublish={canPublish} loadingPublish={loadingPublish} onPublish={publish} />
        )}
        {campaignState === "paused" && wizardFinished && (
          <CampaignStoppedByUserAnnouncement adSystem={adSystem} conversionsCount={conversionsCount} />
        )}
        {campaignState === "account_suspended" && <CampaignStoppedBySystemAnnouncement adSystem={adSystem} />}
        {googleShoppingSuspended && isActiveCampaignState(campaignState) && <GoogleShoppingSuspendedAnnouncement />}
        {adSystem === AdSystemT.GoogleAdsT && allProductsMissingShipping && isRunningCampaignState(campaignState) && (
          <MissingShippingAnnouncement onNavigateToRecommendations={onNavigateToRecommendations} />
        )}
      </div>
      <EditBudgetModal
        adSystem={adSystem}
        initialBudget={editBudgetInitialValue}
        isOpen={showEditBudgetModal}
        onClose={() => {
          setEditBudgetInitialValue(undefined);
          setShowEditBudgetModal(false);
        }}
      />
    </>
  );
};

export default AdSystemBudgetBox;
